import { z } from 'zod';
import { PaginatedResponseSchema, PaginationQuerySchema } from './pagination';

/**
 * Block 14 «Раскрой» — API-контракты `cutting-*` EP-1: projects + drafts.
 *
 * Источник истины — TRD §4.1 (базовые типы), §5.1.9 (ProjectData), §3.2 (таблицы),
 * с разрешением расхождений TRD по решению супервайзера (D1–D4, см.
 * `docs/architecture/adr/0065-cutting-contracts-ep1.md` §Context):
 *
 *   D1/D3 — `CuttingProjectSchema` = верх §4.1 + плоские метаданные
 *     (materialText/materialId/status/customerName?/dueDate?/notes?) + `data`
 *     (ProjectData §5.1.9). Контракт зеркален схеме `@pomoshnik/region-db` →
 *     roundtrip честный.
 *   D2 — `materialText` обязателен (free-text, P-001 — НЕ из каталога в v1, §IX.X #1);
 *     `materialId` — `z.string().nullable()` (Global_ID verbatim, P-001, БЕЗ
 *     нормализации/transform).
 *   D4 — drafts CRUD + promote проектируются ЗДЕСЬ (в TRD §4 нет drafts-эндпоинтов);
 *     `promote` создаёт project из `draft.data` и УДАЛЯЕТ драфт (ADR-0065).
 *
 * Эндпоинт↔метод (GET/POST only — `api-client/client.ts` поддерживает GET/POST,
 * правка существующего клиента запрещена handover §3; идиома idempotent-upsert как
 * у `calculator`, ADR-0019 §Q11.3):
 *   - `GET  /api/cutting/projects`            → список (cursor-пагинация)
 *   - `GET  /api/cutting/projects/:id`        → CuttingProject
 *   - `POST /api/cutting/projects`            → upsert по client-UUID → CuttingProject
 *   - `POST /api/cutting/projects/:id/delete` → soft-delete → DeleteAck
 *   - `GET  /api/cutting/drafts`              → список драфтов (полные, рабочий набор)
 *   - `POST /api/cutting/drafts`              → upsert по client-UUID → CuttingDraft
 *   - `POST /api/cutting/drafts/:id/promote`  → project из draft.data + удалить драфт → CuttingProject
 *
 * Ошибки — общий `ApiError` конверт (`./errors.ts`), парсится `api-client/client.ts`.
 * P-011: контракты заморожены (правка — только новым ADR). P-031: списки отдают
 * метаданные, не полную внутреннюю структуру. P-013: все URL относительные.
 *
 * @see Помощник — Прототип Раскроя/03-tz-developer/03-TRD.md §4.1, §5.1.9, §3.2
 */

// ────────────────────────────────────────────────────────────────────────────
// Базовые типы (TRD §4.1 — вербатим)
// ────────────────────────────────────────────────────────────────────────────

export const RotationModeSchema = z.enum(['allow', 'forbid', 'force']);
export const EdgeTypeSchema = z.enum(['type1', 'type2', 'type3', 'type4', '']);

export const EdgeAssignmentSchema = z.object({
  top: EdgeTypeSchema,
  bottom: EdgeTypeSchema,
  left: EdgeTypeSchema,
  right: EdgeTypeSchema,
});

export const PartSchema = z.object({
  id: z.string(),
  length: z.number().positive().max(10000), // мм
  width: z.number().positive().max(10000),
  qty: z.number().int().positive().max(1000),
  rotation: RotationModeSchema,
  name: z.string().max(200),
  do: z.boolean(),
  doType: z.string().optional(), // 'milling'|'drilling'|'rounding'|'engraving'|'other'
  edges: EdgeAssignmentSchema,
  material: z.string().optional(), // multi-material (free-text, P-001)
});

export const SheetSizeConfigSchema = z.object({
  mode: z.enum(['uniform', 'mixed']),
  uniformSheet: z.object({ length: z.number(), width: z.number() }).optional(),
  mixedVariants: z
    .tuple([
      z.object({ length: z.number(), width: z.number() }),
      z.object({ length: z.number(), width: z.number() }),
    ])
    .optional(),
});

export const CuttingSettingsSchema = z.object({
  kerf: z.number().min(0).max(20).default(4),
  edgeOffset: z.number().min(0).max(50).default(10),
  edgeOverhang: z.number().min(0).max(100).default(50), // v3.12 — §5.1.11
  method: z.enum(['length', 'width', 'optimal']).default('optimal'),
  startPoint: z.enum(['tl', 'tr', 'bl', 'br']).default('tl'),
  preferLongOffcuts: z.boolean().default(true), // §5.1.8
  preferWideOffcuts: z.boolean().default(true), // §5.1.8
  sheetConfig: SheetSizeConfigSchema, // v3.11 — §5.1.9
  // Расширенные опциональные поля production-конфигурации
  offcutEdgeOffset: z.number().nonnegative().max(100).optional(),
  edgeOffsetTop: z.number().nonnegative().optional(),
  edgeOffsetBottom: z.number().nonnegative().optional(),
  edgeOffsetLeft: z.number().nonnegative().optional(),
  edgeOffsetRight: z.number().nonnegative().optional(),
  minStripWidth: z.number().nonnegative().optional(),
  maxStripLength: z.number().positive().optional(),
  maxCarriageRun: z.number().positive().optional(),
  uporLimit: z.number().positive().optional(),
  considerTexture: z.boolean().optional(),
  useOffcutsFirst: z.boolean().optional(),
  machineType: z.enum(['guillotine', 'nesting']).optional(),
  machinePreset: z.string().optional(),
  minOffcutSize: z.number().nonnegative().optional(),
  rounding: z.number().positive().optional(),
  packetMode: z.boolean().optional(),
  materialType: z.enum(['sheet', 'linear']).default('sheet').optional(),
  // Минимальный размер детали, мм (ADR-0087): деталь недопустима, если её короткая
  // сторона < minPartSize. Аддитивно, `.default(40)` (мин. ширина детали ЛДСП ~40мм);
  // 0 = проверка выключена. Валидация чисто web (partSizeStatus), до движка.
  minPartSize: z.number().min(0).max(1000).default(40),
});

// ────────────────────────────────────────────────────────────────────────────
// SheetLayout — геометрия раскладки листа (EP-5). Зеркало внутренних типов движка
// `@pomoshnik/cutting-engine` (`src/types.ts`). До EP-4 поле было `z.unknown()`
// (форма ещё развивалась); после закрытия EP-4 форма стабилизирована и раскрыта
// здесь как конкретная схема — ADR-0078. UI EP-5 (ResultsTab, SVG-карта) и 3
// остаточные метрики получают типизированные, runtime-валидируемые данные.
// Система координат: x — вдоль длины листа, y — вдоль ширины, (0,0) — левый-нижний
// угол; размеры в мм. Контракт ПОДГОНЯЕТСЯ под движок (не наоборот) — форму движка
// менять нельзя без обратной правки этой схемы (см. `contract-mirror.test.ts`).
// ────────────────────────────────────────────────────────────────────────────

/** Осе-ориентированный прямоугольник (`w` — вдоль длины x, `h` — вдоль ширины y). */
export const RectSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
});

/** Размещённая деталь на листе: `x`/`y` — левый-нижний угол, `w`/`h` — габарит (без kerf). */
export const PlacementSchema = z.object({
  partId: z.string(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  rotated: z.boolean(),
});

/** Деталь, отклонённая доменным правилом размещения (§5.3, CUT-4.4a). */
export const PlacementWarningSchema = z.object({
  instanceId: z.string(), // `${part.id}#${k}` — также присутствует в `unplaced`
  reason: z.enum(['maxCarriageRun', 'uporLimit']),
});

/**
 * Раскладка одного использованного листа. Точное зеркало `SheetLayout` движка.
 * `isOffcut`/`offcutId`/`material` присутствуют ТОЛЬКО на листах-остатках (§5.1.8);
 * целые листы этих полей не имеют (back-compat существующих снапшотов).
 */
export const SheetLayoutSchema = z.object({
  sheetId: z.string(),
  sheetIndex: z.number().int(),
  sheetLength: z.number(),
  sheetWidth: z.number(),
  placements: z.array(PlacementSchema),
  freeRects: z.array(RectSchema),
  isOffcut: z.boolean().optional(),
  offcutId: z.string().optional(),
  material: z.string().optional(),
});

/**
 * Результат раскроя. Зеркало engine `Layout` (`{ sheets, unplaced, warnings,
 * metrics }`) — `OptimizationResultSchema.parse(engineLayout)` проходит напрямую
 * (см. `contract-mirror.test.ts`). До ADR-0078 `sheets[].layout` был `z.unknown()`;
 * теперь `sheets` — плоский массив `SheetLayoutSchema` (без wrapper-обёртки и
 * дублирования `sheetId`). `warnings` — причины неразмещения (§5.3, CUT-5.5);
 * `.default([])` держит back-compat со снапшотами до ADR-0078.
 */
export const OptimizationResultSchema = z.object({
  sheets: z.array(SheetLayoutSchema),
  metrics: z.object({
    kim: z.number().min(0).max(1),
    wastePercent: z.number(),
    schemeCount: z.number().int(),
    cutLengthMeters: z.number(),
    cutCount: z.number().int(),
    packageCount: z.number().int(),
  }),
  unplaced: z.array(z.string()), // partId[]
  warnings: z.array(PlacementWarningSchema).default([]),
});

/** Запись остатка (cutting_warehouse / `pomoshnik_offcuts_v1`, TRD §4.1). */
export const OffcutSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  length: z.number().positive(),
  width: z.number().positive(),
  qty: z.number().int().positive(),
  material: z.string(), // free-text (P-001)
  sourceProjectId: z.string().uuid().optional(),
  createdAt: z.string().datetime(),
});

/**
 * Кромочные ленты проекта (TRD §3.2 `edges`: `{ type1, type2, type3?, type4? }`).
 * §4.1 не задаёт тип значений — берём строковую метку материала кромки
 * (например 'ПВХ 2мм Венге'); пустая строка = лента не задана. ⚠️ STOP-1 review:
 * структура из §3.2, тип значения (string) — вывод, подтвердить (ADR-0065 §Context).
 */
export const EdgesConfigSchema = z.object({
  type1: z.string(),
  type2: z.string(),
  type3: z.string().optional(),
  type4: z.string().optional(),
});

// ────────────────────────────────────────────────────────────────────────────
// ProjectData — рабочая нагрузка проекта/драфта (TRD §5.1.9 + D3)
// ────────────────────────────────────────────────────────────────────────────

export const ProjectDataSchema = z.object({
  settings: CuttingSettingsSchema,
  sheetConfig: SheetSizeConfigSchema, // отдельно от settings (§5.1.9)
  parts: z.array(PartSchema),
  edges: EdgesConfigSchema,
  offcuts: z.array(OffcutSchema),
  /**
   * Id записей склада остатков (device-local `pomoshnik_offcuts_v1`), отмеченных
   * «участвует в раскрое» для этого проекта/черновика (per-project выбор, ADR-0081).
   * Аддитивно/back-compat (`.default([])`); P-001 — id verbatim. Резолв на раскрой
   * пропускает отсутствующие (удалённые из склада) id — dangling выбор не крэшит.
   */
  participatingOffcutIds: z.array(z.string()).default([]),
  lastResult: OptimizationResultSchema.optional(), // только после раскроя
  estimate: z.unknown().optional(), // ProjectEstimate (v3-spec, EP-7) — opaque (P-031)
  checksum: z.string(), // MD5 от рабочей нагрузки (целостность)
});

export const CuttingProjectStatusSchema = z.enum(['draft', 'sent', 'completed', 'archived']);

// ────────────────────────────────────────────────────────────────────────────
// Projects (TRD §4.2 + D1/D3)
// ────────────────────────────────────────────────────────────────────────────

/** Полная запись проекта (Response). Зеркало `region-db.cuttingProjects`. */
export const CuttingProjectSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(), // server-side
  name: z.string().max(200),
  materialText: z.string().min(1).max(500), // D2: обязательный free-text
  materialId: z.string().nullable(), // D2/P-001: Global_ID verbatim либо null
  status: CuttingProjectStatusSchema,
  customerName: z.string().max(200).optional(),
  dueDate: z.string().date().optional(),
  notes: z.string().max(2000).optional(),
  data: ProjectDataSchema,
  createdAt: z.string().datetime(), // server-side
  updatedAt: z.string().datetime(), // server-side
});

/**
 * Upsert проекта (Request `POST /api/cutting/projects`). Idempotent по
 * client-generated `id` (ADR-0019 §Q11.3 идиома). Security (handover §7.2, P-010):
 * `userId`/`createdAt`/`updatedAt` НЕ принимаются с фронта (server-side).
 */
export const CuttingProjectUpsertSchema = z.object({
  id: z.string().uuid(), // client-generated UUID (идемпотентность)
  name: z.string().max(200),
  materialText: z.string().min(1).max(500),
  materialId: z.string().nullable().default(null), // P-001 verbatim
  status: CuttingProjectStatusSchema.default('draft'),
  customerName: z.string().max(200).optional(),
  dueDate: z.string().date().optional(),
  notes: z.string().max(2000).optional(),
  data: ProjectDataSchema,
});

/** Элемент списка проектов (метаданные, P-031 — без полной `data`). */
export const CuttingProjectListItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  materialText: z.string(),
  status: CuttingProjectStatusSchema,
  customerName: z.string().optional(),
  lastKim: z.number().min(0).max(1).optional(), // КИМ последнего раскроя
  sheetCount: z.number().int().nonnegative().optional(),
  updatedAt: z.string().datetime(),
});

export const CuttingProjectListResponseSchema = PaginatedResponseSchema(
  CuttingProjectListItemSchema,
);

/** Query `GET /api/cutting/projects` (cursor-пагинация репо + фильтры EP-1). */
export const CuttingProjectListQuerySchema = PaginationQuerySchema.extend({
  status: CuttingProjectStatusSchema.optional(),
  search: z.string().max(200).optional(), // name / customer
});

/** Ответ soft-delete (`POST /api/cutting/projects/:id/delete`). */
export const CuttingDeleteAckSchema = z.object({
  id: z.string().uuid(),
  deleted: z.literal(true),
});

// ────────────────────────────────────────────────────────────────────────────
// Drafts (D4 — спроектированы здесь; в TRD §4 эндпоинтов нет)
// ────────────────────────────────────────────────────────────────────────────

/** Полная запись драфта (Response). Зеркало `region-db.cuttingDrafts` (без status). */
export const CuttingDraftSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(), // server-side
  name: z.string().max(200),
  data: ProjectDataSchema,
  createdAt: z.string().datetime(), // server-side
  updatedAt: z.string().datetime(), // server-side
});

/**
 * Upsert драфта (`POST /api/cutting/drafts`). Idempotent по client-UUID.
 * Security: `userId`/`createdAt`/`updatedAt` server-side, с фронта не принимаются.
 */
export const CuttingDraftUpsertSchema = z.object({
  id: z.string().uuid(),
  name: z.string().max(200),
  data: ProjectDataSchema,
});

/** Список драфтов (полные — рабочий набор для восстановления, §6.5). */
export const CuttingDraftListResponseSchema = PaginatedResponseSchema(CuttingDraftSchema);

/**
 * Promote драфта в проект (`POST /api/cutting/drafts/:id/promote`, D4).
 * Поведение (ADR-0065): создать project из `draft.data`, УДАЛИТЬ драфт, вернуть
 * созданный project. В драфте нет метаданных проекта → клиент передаёт
 * обязательный `materialText` (D2) и опциональные метаданные; `name` — из драфта
 * либо override.
 */
export const CuttingDraftPromoteSchema = z.object({
  materialText: z.string().min(1).max(500), // D2: обязателен у проекта
  materialId: z.string().nullable().default(null), // P-001 verbatim
  status: CuttingProjectStatusSchema.default('draft'),
  customerName: z.string().max(200).optional(),
  dueDate: z.string().date().optional(),
  notes: z.string().max(2000).optional(),
  name: z.string().max(200).optional(), // override имени драфта
});

// ────────────────────────────────────────────────────────────────────────────
// Типы
// ────────────────────────────────────────────────────────────────────────────

export type RotationMode = z.infer<typeof RotationModeSchema>;
export type EdgeType = z.infer<typeof EdgeTypeSchema>;
export type EdgeAssignment = z.infer<typeof EdgeAssignmentSchema>;
export type Part = z.infer<typeof PartSchema>;
export type SheetSizeConfig = z.infer<typeof SheetSizeConfigSchema>;
export type CuttingSettings = z.infer<typeof CuttingSettingsSchema>;
export type Rect = z.infer<typeof RectSchema>;
export type Placement = z.infer<typeof PlacementSchema>;
export type PlacementWarning = z.infer<typeof PlacementWarningSchema>;
export type SheetLayout = z.infer<typeof SheetLayoutSchema>;
export type OptimizationResult = z.infer<typeof OptimizationResultSchema>;
/** Вход optimization-result (warnings можно опускать — дефолтит в []). */
export type OptimizationResultInput = z.input<typeof OptimizationResultSchema>;
export type Offcut = z.infer<typeof OffcutSchema>;
export type EdgesConfig = z.infer<typeof EdgesConfigSchema>;
export type ProjectData = z.infer<typeof ProjectDataSchema>;
export type CuttingProjectStatus = z.infer<typeof CuttingProjectStatusSchema>;
export type CuttingProject = z.infer<typeof CuttingProjectSchema>;
export type CuttingProjectUpsert = z.infer<typeof CuttingProjectUpsertSchema>;
/** Вход upsert (дефолты `materialId`/`status` можно опускать — применятся при parse). */
export type CuttingProjectUpsertInput = z.input<typeof CuttingProjectUpsertSchema>;
export type CuttingProjectListItem = z.infer<typeof CuttingProjectListItemSchema>;
export type CuttingProjectListResponse = z.infer<typeof CuttingProjectListResponseSchema>;
export type CuttingProjectListQuery = z.infer<typeof CuttingProjectListQuerySchema>;
export type CuttingDeleteAck = z.infer<typeof CuttingDeleteAckSchema>;
export type CuttingDraft = z.infer<typeof CuttingDraftSchema>;
export type CuttingDraftUpsert = z.infer<typeof CuttingDraftUpsertSchema>;
export type CuttingDraftListResponse = z.infer<typeof CuttingDraftListResponseSchema>;
export type CuttingDraftPromote = z.infer<typeof CuttingDraftPromoteSchema>;
/** Вход promote (дефолты `materialId`/`status` можно опускать). */
export type CuttingDraftPromoteInput = z.input<typeof CuttingDraftPromoteSchema>;
